import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useDeleteBlock } from '../../../../../hooks/block/useDeleteBlock'

import { DropdownMenuItem } from '../../../../dropdownMenu/DropdownMenu'
import { RowActions } from '../../../../listItem/rowActions/RowActions'
import { ListRow } from '../../../../listItem/listRow/ListRow'
import { ListCard } from '../../../../listItem/listCard/ListCard'
import { Tabs } from '../../../../tabs/Tabs'
import { ListingDetails } from '../../../../listing/ListingDetails'

import { useApp } from '../../../../../contexts/AppContext'
import { useConfirm } from '../../../../../contexts/ConfirmContext'
import { CONFIG } from '../../../../../config'
import {
  BlockFilter,
  BlockListProps,
  BlockModel,
  BlockReason,
} from '../../../../../types/court/court'
import { ConfirmLayout } from '../../../../../types/confirm/confirm'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import { DisplayType } from '../../../../../types/listing'
import {
  toDisplayTimeAndDate,
  toValidDate,
} from '../../../../../utils/dateUtils'


const BLOCK_REASON_BADGE_CLASS: Record<BlockReason, string> = {
  [BlockReason.MAINTENANCE]: 'badge-warning',
  [BlockReason.VACATION]: 'badge-info',
  [BlockReason.PRIVATE_EVENT]: 'badge-accent',
  [BlockReason.TOURNAMENT]: 'badge-success',
  [BlockReason.BAD_WEATHER]: 'badge-danger',
}

const isBlockActive = (block: BlockModel): boolean =>
  (toValidDate(block.endTime)?.getTime() ?? -Infinity) >= Date.now()

/**
 * Blocks of every court, paged by the server, with the same listing as the booking history: search by court, list or
 * card view, and an upcoming / passed / all filter.
 */
export const BlockList = ({ refreshTrigger, onChanged }: BlockListProps) => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { t, i18n } = useTranslation(['blocks', 'exceptions'])
  const [filter, setFilter] = useState(BlockFilter.UPCOMING)

  const { deleteBlock, loading, resetState } = useDeleteBlock()

  const tabs = Object.values(BlockFilter).map(value => ({
    value,
    label: t(`blocks.list.filter.${value.toLowerCase()}`),
  }))

  const handleDelete = (block: BlockModel) => {
    const blockId = block.id

    if (blockId === undefined) {
      return
    }

    const reason = t(`blocks.reasons.${block.reason}`, {
      defaultValue: block.reason,
    })

    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('blocks.delete-confirmation.title'),
      subtitle: t('blocks.delete-confirmation.subtitle'),
      confirmLabel: t('blocks.actions.remove'),
      backLabel: t('blocks.actions.cancel'),
      returnTo: '/m/blocks',
      itemCards: [
        {
          id: blockId,
          title: block.courtName ?? '—',
          details: [
            {
              label: t('blocks.list.from'),
              value: toDisplayTimeAndDate(block.startTime, i18n.language) ?? '',
            },
            {
              label: t('blocks.list.to'),
              value: toDisplayTimeAndDate(block.endTime, i18n.language) ?? '',
            },
            {
              label: t('blocks.list.reason'),
              value: reason,
            },
          ],
        },
      ],
      onConfirm: async () => {
        try {
          await deleteBlock(block.courtId, blockId)

          showBanner({
            kind: BannerKind.OK,
            msg: t('blocks.successfully-deleted'),
          })

          resetState()
          await onChanged()
        } catch (caughtError) {
          showBanner({
            kind: BannerKind.ERR,
            msg:
              caughtError instanceof Error
                ? caughtError.message
                : t('exceptions:unknown'),
          })

          resetState()
        }
      },
    })
  }

  const menuItems = (block: BlockModel): DropdownMenuItem[] =>
    isBlockActive(block)
      ? [
          {
            key: 'edit',
            label: t('blocks.actions.edit'),
            icon: 'edit',
            onClick: () =>
              navigate(`/m/blocks/${block.id}/edit?courtId=${block.courtId}`),
          },
          {
            key: 'remove',
            label: t('blocks.actions.remove'),
            icon: 'trash',
            danger: true,
            disabled: loading,
            onClick: () => handleDelete(block),
          },
        ]
      : []

  const renderActions = (block: BlockModel) => (
    <RowActions label={t('blocks.actions.more')} items={menuItems(block)} />
  )

  const renderBlock = (block: BlockModel, viewMode: DisplayType) => {
    const reason = t(`blocks.reasons.${block.reason}`, {
      defaultValue: block.reason,
    })
    const reasonBadge = (
      <span
        className={`badge ${BLOCK_REASON_BADGE_CLASS[block.reason as BlockReason] ?? ''}`}
      >
        {reason}
      </span>
    )
    const active = isBlockActive(block)
    const statusBadge = (
      <span className={`badge ${active ? 'badge-success' : 'badge-warning'}`}>
        {t(active ? 'blocks.status.active' : 'blocks.status.expired')}
      </span>
    )
    const description = block.notes?.trim()
    const fromLabel = toDisplayTimeAndDate(block.startTime, i18n.language)
    const toLabel = toDisplayTimeAndDate(block.endTime, i18n.language)

    if (viewMode === DisplayType.LIST) {
      return (
        <ListRow
          key={block.id}
          cells={[
            {
              key: 'court',
              content: (
                <div className="fw-semibold">{block.courtName ?? '—'}</div>
              ),
            },
            {
              key: 'from',
              className: 'mono',
              content: fromLabel,
            },
            {
              key: 'to',
              className: 'mono',
              content: toLabel,
            },
            {
              key: 'reason',
              content: reasonBadge,
            },
            {
              key: 'description',
              content: description ? (
                <div
                  className="muted text-truncate"
                  style={{ maxWidth: 260 }}
                  title={description}
                >
                  {description}
                </div>
              ) : (
                <span className="muted">—</span>
              ),
            },
            {
              key: 'status',
              content: statusBadge,
            },
          ]}
          actions={renderActions(block)}
        />
      )
    }

    return (
      <ListCard
        key={block.id}
        stripe={active ? 'success' : 'neutral'}
        title={block.courtName ?? '—'}
        subtitle={reason}
        status={statusBadge}
        menu={renderActions(block)}
        muted={!active}
        fields={[
          { key: 'from', label: t('blocks.list.from'), value: fromLabel },
          { key: 'to', label: t('blocks.list.to'), value: toLabel },
          ...(description
            ? [
                {
                  key: 'description',
                  label: t('blocks.list.description'),
                  value: description,
                  mono: false,
                  wide: true,
                },
              ]
            : []),
        ]}
      />
    )
  }

  return (
    <div className="block-list">
      <h3 className="mb-16">{t('blocks.list.title')}</h3>

      <ListingDetails<BlockModel>
        endpoint={`${CONFIG.BOUNCE_API}/api/v1/blocks`}
        initialViewMode={DisplayType.LIST}
        displaySearch
        searchPlaceholder={t('blocks.list.search-placeholder')}
        filter={
          <Tabs
            tab={filter}
            tabs={tabs}
            onChange={value => setFilter(value as BlockFilter)}
          />
        }
        additionalParams={{ filter }}
        refreshTrigger={refreshTrigger}
        tableColumns={[
          {
            key: 'court',
            label: t('blocks.list.court'),
          },
          {
            key: 'from',
            label: t('blocks.list.from'),
          },
          {
            key: 'to',
            label: t('blocks.list.to'),
          },
          {
            key: 'reason',
            label: t('blocks.list.reason'),
          },
          {
            key: 'description',
            label: t('blocks.list.description'),
          },
          {
            key: 'status',
            label: t('blocks.list.status'),
          },
          {
            key: 'actions',
            label: t('blocks.list.actions'),
            align: 'end',
          },
        ]}
        renderItem={renderBlock}
      />
    </div>
  )
}
